import React, { useState } from "react";
import api from "@/lib/api";
import { Calendar as CalendarIcon, Clock, User as UserIcon, Phone, MessageSquare, CheckCircle2, XCircle } from "lucide-react";
import { toast } from "sonner";

function prettyDate(iso) {
  if (!iso) return "";
  const [y, m, d] = iso.split("-");
  const dt = new Date(Number(y), Number(m) - 1, Number(d));
  return dt.toLocaleDateString("en-IN", { weekday: "short", day: "numeric", month: "short" });
}

export default function UpcomingSiteVisits({ visits = [], onStatusChange }) {
  const [busyId, setBusyId] = useState(null);

  const setStatus = async (visit_id, status) => {
    setBusyId(visit_id);
    try {
      await api.post(`/site-visits/${visit_id}/status`, { status });
      toast.success(status === "confirmed" ? "Visit confirmed" : status === "completed" ? "Marked complete" : "Cancelled");
      onStatusChange?.(visit_id, status);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update visit");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="sg-card-cream p-5" data-testid="upcoming-site-visits">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="overline text-sg-gold-deep">UPCOMING SITE VISITS</div>
          <div className="text-xs text-sg-mute mt-0.5">Booked via InProp AI · routes straight to you</div>
        </div>
        <div className="w-10 h-10 rounded-full bg-white border border-sg-line flex items-center justify-center">
          <CalendarIcon className="w-4 h-4 text-sg-gold-deep" />
        </div>
      </div>

      <div className="space-y-2">
        {visits.map((v) => (
          <div
            key={v.visit_id}
            className="bg-white rounded-xl border border-sg-line p-3 flex flex-wrap gap-3 items-start hover:border-sg-gold transition-colors"
            data-testid={`site-visit-${v.visit_id}`}
          >
            {/* Date pill */}
            <div className="flex flex-col items-center justify-center w-14 h-14 rounded-lg bg-sg-navy text-white flex-shrink-0">
              <div className="text-[9px] uppercase tracking-wider opacity-70">
                {prettyDate(v.preferred_date).split(",")[0]}
              </div>
              <div className="text-lg font-bold leading-none">
                {v.preferred_date?.split("-")[2]}
              </div>
              <div className="text-[9px] uppercase tracking-wider opacity-80">
                {new Date(Number(v.preferred_date?.split("-")[0]), Number(v.preferred_date?.split("-")[1]) - 1).toLocaleDateString("en-IN", { month: "short" })}
              </div>
            </div>

            {/* Body */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <div className="font-semibold text-sg-navy text-sm truncate">{v.name}</div>
                <span className={`text-[9px] px-1.5 py-0.5 rounded-full uppercase tracking-wider font-semibold ${
                  v.status === "confirmed" ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                  : "bg-amber-50 text-amber-800 border border-amber-200"
                }`}>
                  {v.status}
                </span>
                <span className="inline-flex items-center gap-1 text-[11px] text-sg-navy/80">
                  <Clock className="w-3 h-3" /> {v.time_slot}
                </span>
              </div>
              {v.project_ref && (
                <div className="text-[11px] text-sg-mute mt-0.5 truncate">Interested in <span className="text-sg-navy font-medium">{v.project_ref}</span></div>
              )}
              <div className="flex items-center gap-3 mt-1.5 text-[11px] text-sg-navy/80">
                <a href={`tel:${v.phone}`} className="inline-flex items-center gap-1 hover:text-sg-gold-deep">
                  <Phone className="w-3 h-3" /> {v.phone}
                </a>
                {v.whatsapp && (
                  <a
                    href={`https://wa.me/${(v.whatsapp || "").replace(/\D/g, "")}`}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1 hover:text-emerald-700"
                  >
                    <MessageSquare className="w-3 h-3" /> WhatsApp
                  </a>
                )}
              </div>
              {v.notes && <div className="text-[11px] text-sg-mute mt-1 italic">&ldquo;{v.notes}&rdquo;</div>}
            </div>

            {/* Actions */}
            <div className="flex flex-col gap-1.5 flex-shrink-0">
              {v.status !== "confirmed" && (
                <button
                  type="button"
                  onClick={() => setStatus(v.visit_id, "confirmed")}
                  disabled={busyId === v.visit_id}
                  className="text-[10px] uppercase tracking-wider font-semibold px-2.5 h-7 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white flex items-center gap-1 disabled:opacity-50"
                  data-testid={`site-visit-${v.visit_id}-confirm`}
                >
                  <CheckCircle2 className="w-3 h-3" /> Confirm
                </button>
              )}
              <button
                type="button"
                onClick={() => setStatus(v.visit_id, "cancelled")}
                disabled={busyId === v.visit_id}
                className="text-[10px] uppercase tracking-wider px-2.5 h-7 rounded-full border border-sg-line text-sg-mute hover:text-sg-navy hover:border-sg-navy flex items-center gap-1 disabled:opacity-50"
                data-testid={`site-visit-${v.visit_id}-cancel`}
              >
                <XCircle className="w-3 h-3" /> Cancel
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
